import { DEFAULT_FILE_PREFIX, extractRouteDocumentId, pathnameIncludesSegment } from './routePath';
import { FileType } from './types';

export type DocumentAutoOpenCheck = (
  baseUrl: string,
  file: FileType,
  prefix?: string
) => boolean;

export function createDocumentAutoOpenCheck(
  pathname: string,
  pathMarkers: readonly string[]
): DocumentAutoOpenCheck {
  return (_baseUrl, file, prefix = DEFAULT_FILE_PREFIX) => {
    const documentId = file?._id;
    if (!documentId) {
      return false;
    }

    const routeDocumentId = extractRouteDocumentId(pathname, prefix);
    if (routeDocumentId !== documentId) {
      return false;
    }

    return pathMarkers.some((marker) =>
      pathnameIncludesSegment(pathname, marker)
    );
  };
}

export function createComplianceCheckAutoOpenCheck(
  pathname: string,
  checklistItemId: string,
  protocolItemId?: string
): DocumentAutoOpenCheck {
  const pathMarkers = [`/checklist/${checklistItemId}`];
  if (protocolItemId) {
    pathMarkers.push(
      `/protocol/${protocolItemId}`,
      `/protocolItem/${protocolItemId}/complianceCheck`
    );
  }
  return createDocumentAutoOpenCheck(pathname, pathMarkers);
}

export function createGeneralNormAutoOpenCheck(
  pathname: string,
  inspectionId: string,
  templateId: string
): DocumentAutoOpenCheck {
  return createDocumentAutoOpenCheck(pathname, [
    `/inspection/${inspectionId}/document`,
    `/inspection/${inspectionId}/template/${templateId}/document`,
    `/inspectionTemplate/${templateId}/generalNorm`,
    `/inspectionTemplate/${templateId}/document`,
  ]);
}

export function createTemplateGeneralNormAutoOpenCheck(
  pathname: string,
  templateId: string
): DocumentAutoOpenCheck {
  return createDocumentAutoOpenCheck(pathname, [
    `/inspectionTemplate/${templateId}/generalNorm`,
    `/inspectionTemplate/${templateId}/document`,
  ]);
}

export function createChecklistItemTemplateAutoOpenCheck(
  pathname: string,
  inspectionId: string,
  templateId: string,
  checklistItemId: string
): DocumentAutoOpenCheck {
  return createDocumentAutoOpenCheck(pathname, [
    `/inspection/${inspectionId}/checklist/${checklistItemId}`,
    `/inspectionTemplate/${templateId}/checklistItem/${checklistItemId}`,
    `/inspectionTemplate/${templateId}/checklist/${checklistItemId}`,
  ]);
}

export function createSectionTemplateAutoOpenCheck(
  pathname: string,
  inspectionId: string | undefined,
  templateId: string,
  sectionId: string
): DocumentAutoOpenCheck {
  const pathMarkers = [
    `/inspectionTemplate/${templateId}/section/${sectionId}`,
  ];

  if (inspectionId) {
    pathMarkers.unshift(
      `/inspection/${inspectionId}/template/${templateId}/section/${sectionId}`
    );
  }

  return createDocumentAutoOpenCheck(pathname, pathMarkers);
}

export function createProtocolSectionAutoOpenCheck(
  pathname: string,
  inspectionId: string,
  protocolSectionId: string
): DocumentAutoOpenCheck {
  return createDocumentAutoOpenCheck(pathname, [
    `/inspection/${inspectionId}/section/${protocolSectionId}`,
  ]);
}
